/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * attachApi — what an organizer may attach to their own race, and attaching it.
 *
 * WHY A SAVED QUERY AND NOT AN ORDINARY READ. `activity` is private and not
 * global, so an organizer reading it directly gets only their OWN runs — probed
 * 2026-11-02, and it is the same failure the declare page had before
 * organizer_race_results existed. The saved query is the only way to see a
 * registrant's runs at all.
 *
 * WHAT GUARDS IT. The query's ownership test is {{caller_filter}} — the
 * signed-in caller's own id, bound, which cannot widen. It means "the race I
 * created", so an ordinary athlete gets zero rows even for a race they are
 * registered for. An earlier cut used the group-based ownership clause instead,
 * which does NOT restrict a caller with no group, and handed one athlete
 * another athlete's private run. Do not go back to it.
 *
 * NOTHING HERE IS A PERMISSION CHECK. The server decides. This module asks, and
 * an empty answer means "not your race, or nobody left to attach" — which is
 * also exactly what the page should draw in both cases.
 */

import envConfig from '../../../config/envConfig';
import { attachRunToRace, resultPayloadFor, RESULT_TABLE, readError } from './resultsApi';

/** The saved query, seeded by `seed_analytics_queries`. */
export const ATTACHABLE_QUERY = 'organizer_attachable_runs';

/** An analytics answer ({columns, rows}) as objects keyed by column name. */
export function rowsAsObjects(payload) {
  const columns = (payload?.columns || []).map((c) => c.name);
  return (payload?.rows || []).map(
    (row) => Object.fromEntries(columns.map((c, i) => [c, row[i]])),
  );
}

/**
 * Everyone on this race who has no result yet, each with the runs that could
 * become one.
 *
 * The query answers one row per registrant per candidate run, and one row with
 * a null activity when they have no candidate at all — so grouping here is what
 * turns it back into people. A registrant with an empty `runs` list is a real
 * answer, not a gap: they entered and have nothing we can attach.
 *
 * `minDistanceMeters` is the category's distance. A run has to cover it ON ITS
 * OWN — stitching several runs together to reach 3 km is not a 3 km result.
 * Pass 0 to see every candidate.
 */
export async function fetchAttachables(api, {
  eventActivityId, minDistanceMeters = 0, signal,
} = {}) {
  if (eventActivityId === null || eventActivityId === undefined) return [];
  const resp = await api.apiCall(
    `/arcee/application/${Number(envConfig.applicationId)}/analytics/${ATTACHABLE_QUERY}/run/`
    + `?event_activity_id=${encodeURIComponent(eventActivityId)}`
    + `&min_distance_meters=${encodeURIComponent(Number(minDistanceMeters) || 0)}`,
    { method: 'GET', signal },
  );
  // A refusal is not an error worth shouting about: the commonest cause is a
  // visitor who does not own this race, and the page simply draws nothing.
  if (!resp.ok) return [];
  return groupByRegistrant(rowsAsObjects(await resp.json()));
}

/**
 * One entry per registrant, runs fastest-first.
 *
 * Fastest first because that is how a category is ranked: anyone who covered
 * the distance is ordered by TIME, and the extra distance somebody chose to run
 * earns them nothing. So the first run in the list is the one to offer.
 */
export function groupByRegistrant(rows) {
  const people = new Map();
  (rows || []).forEach((row) => {
    const key = String(row.registration_id);
    if (!people.has(key)) {
      people.set(key, {
        registrationId: row.registration_id,
        athleteUserId: row.athlete_user_id,
        athleteHandle: row.athlete_handle || '',
        runs: [],
      });
    }
    if (row.activity_id) {
      people.get(key).runs.push({
        id: row.activity_id,
        activity_type: row.activity_type,
        started_at: row.started_at,
        distance_meters: Number(row.distance_meters),
        duration_seconds: Number(row.duration_seconds),
      });
    }
  });
  const everyone = [...people.values()];
  everyone.forEach((person) => {
    person.runs.sort((a, b) => {
      const left = Number.isFinite(a.duration_seconds) ? a.duration_seconds : Infinity;
      const right = Number.isFinite(b.duration_seconds) ? b.duration_seconds : Infinity;
      return left - right;
    });
  });
  return everyone;
}

/**
 * "meera rao" -> "Meera Rao". Leaves anything already capitalised alone.
 *
 * A name goes onto a public results board and is frozen there, so it is worth
 * tidying once rather than living with "meera rao" under a podium forever.
 * O'Brien and Rao-Shetty keep their inner capitals: only the first letter of
 * each word is touched, never the rest.
 */
export function titleCase(value) {
  return String(value || '')
    .trim()
    .split(/\s+/)
    .map((word) => (word ? word[0].toLocaleUpperCase() + word.slice(1) : word))
    .join(' ');
}

/**
 * The names these athletes are known by, from their own past results.
 *
 * WHY HERE AND NOT IN THE QUERY. A result carries a frozen COPY of the name,
 * and no table holds a person's real name — the names on the board were
 * written by athletes from their own sessions. Reading them back inside the
 * saved query was refused by the platform, correctly: a projected subquery
 * over an app table carries no tenant filter. But event_result is PUBLIC and
 * the board already reads it, so the page can simply look them up.
 *
 * Returns a Map of athlete user id -> name, newest result wins. Athletes with
 * no past result are absent, and the caller falls back to their handle.
 */
export async function fetchKnownNames(api, { userIds, signal } = {}) {
  const ids = [...new Set((userIds || []).filter((id) => id !== null && id !== undefined))];
  if (ids.length === 0) return new Map();
  const resp = await api.publicApiCall('/arcee/application/public/table/fetch/', {
    method: 'POST',
    signal,
    body: JSON.stringify({
      organization_id: Number(envConfig.organizationId),
      application_id: Number(envConfig.applicationId),
      table_name: 'event_result',
      filters: [{ column_key: 'created_by_id', filter_type: 'in', column_value: ids }],
      ordering: '-id',
      page: 1,
      page_size: 500,
    }),
  });
  if (!resp.ok) return new Map();
  const payload = await resp.json();
  const rows = Array.isArray(payload) ? payload : (payload?.records || payload?.results || []);
  const names = new Map();
  rows.forEach((row) => {
    const owner = row.created_by_id && (row.created_by_id.id ?? row.created_by_id);
    const name = String(row.athlete_name || '').trim();
    // Ordered newest first, so the first one seen for a person is the latest.
    if (owner !== undefined && owner !== null && name && !names.has(String(owner))) {
      names.set(String(owner), titleCase(name));
    }
  });
  return names;
}

/** The run to offer for this registrant, or null when they have none. */
export function bestRunFor(person) {
  return (person && person.runs && person.runs[0]) || null;
}

/**
 * Rows per batch, and the number is set by the HOOK, not by the batch endpoint.
 *
 * The endpoint itself would take 500 (MAX_ROWS). What cannot is the pre-save
 * validation hook: it runs synchronously in the request thread under
 * PRE_HOOK_TIMEOUT, five seconds by default, and it validates every row it is
 * handed. Measured 2026-11-03 against a 67-entrant race: 67 rows in one batch
 * blew the timeout and the whole batch was blocked, 25 rows went through
 * comfortably in three batches with no fallback.
 *
 * So this is a budget, not a preference. Raise it only with the hook in front
 * of you, and re-measure — the cost is per row and the hook is free to grow.
 */
export const ATTACH_BATCH = 25;

function scope() {
  return {
    organization_id: Number(envConfig.organizationId),
    application_id: Number(envConfig.applicationId),
  };
}

/** One batch of inserts. Resolves on success, throws the server's sentence. */
async function writeBatch(api, rows) {
  const resp = await api.apiCall('/arcee/application/table/save/records/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...scope(),
      operations: [{ table: RESULT_TABLE, data: rows }],
    }),
  });
  if (!resp.ok) throw new Error(await readError(resp, 'Could not add these runs to the race.'));
  return resp.json().catch(() => ({}));
}

/** The slow path: one request per pick, so each refusal names its own athlete. */
async function attachOneByOne(api, picks, { eventActivityId, timingSourceId }) {
  const attached = [];
  const refused = [];
  for (const pick of picks) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await attachRunToRace(api, {
        registrationId: pick.registrationId,
        activity: pick.run,
        timingSourceId,
        eventActivityId,
        athleteName: pick.athleteName,
      });
      attached.push(pick.registrationId);
    } catch (e) {
      refused.push({
        registrationId: pick.registrationId,
        athleteName: pick.athleteName,
        reason: (e && e.message) || 'Could not attach that run.',
      });
    }
  }
  return { attached, refused };
}

/**
 * Attaches one chosen run per registrant, in batches.
 *
 * WHY BATCHES. This used to send one request per athlete, sequentially. On a
 * five-person race that was invisible; on a thousand-entrant race it is a
 * thousand round trips, and a tab closed at six hundred leaves the job half
 * done with no way to tell where. One batch of 200 is one request.
 *
 * WHY A FALLBACK, AND WHY IT IS NOT OPTIONAL. A batch is one transaction:
 * everything lands or nothing does. That is the right guarantee for the rows
 * but the wrong REPORT, because the panel's whole contract is "17 attached, 1
 * refused, and here is why" — one entrant whose Drive cannot be attached to a
 * Run race must not cost the other 199 their attach. So a batch that is refused
 * is retried one at a time, which costs an extra pass only on the batches that
 * actually contain a bad row, and names the athlete responsible.
 *
 * The validation hook still runs either way: the platform fires
 * record.before_created for a batch too, and the hook reads a list as happily
 * as a single row.
 */
export async function attachMany(api, {
  picks, eventActivityId, timingSourceId,
  // Called after each batch with { done, total }. Optional, because the tests
  // call this without one — but the panel passes it, and should: a 500-run
  // attach is over a minute of work, and a loader that cannot say how far it
  // has got is only marginally better than the button label it replaced.
  onProgress,
} = {}) {
  const all = picks || [];
  const attached = [];
  const refused = [];

  for (let i = 0; i < all.length; i += ATTACH_BATCH) {
    const slice = all.slice(i, i + ATTACH_BATCH);
    const rows = slice.map((pick) => resultPayloadFor({
      registrationId: pick.registrationId,
      activity: pick.run,
      timingSourceId,
      eventActivityId,
      athleteName: pick.athleteName,
    }));

    try {
      // eslint-disable-next-line no-await-in-loop
      await writeBatch(api, rows);
      slice.forEach((pick) => attached.push(pick.registrationId));
    } catch (e) {
      // Nothing in this batch was written — find out which row spoiled it.
      // eslint-disable-next-line no-await-in-loop
      const retry = await attachOneByOne(api, slice, { eventActivityId, timingSourceId });
      attached.push(...retry.attached);
      refused.push(...retry.refused);
    }

    // Reported after the batch has settled, success or refusal, so the figure
    // counts work DONE rather than work started.
    if (onProgress) {
      onProgress({ done: Math.min(i + ATTACH_BATCH, all.length), total: all.length });
    }
  }

  return { attached, refused };
}
